import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocationProvider } from './context/LocationContext';
import { CategoriesProvider } from './context/CategoriesContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Billing from './pages/Billing';
import Sales from './pages/Sales';
import DayClose from './pages/DayClose';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import RawMaterials from './pages/RawMaterials';
import Recipes from './pages/Recipes';
import Production from './pages/Production';
import Packing from './pages/Packing';
import Transfers, { StockRequests } from './pages/Transfers';
import Procurement from './pages/Procurement';
import Returns from './pages/Returns';
import Labels from './pages/Labels';
import Suppliers from './pages/Suppliers';
import SupplierDetail from './pages/SupplierDetail';
import Inventory from './pages/Inventory';
import Locations from './pages/Locations';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import RawMaterialDetail from './pages/RawMaterialDetail';
import RecipeDetail from './pages/RecipeDetail';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Payroll from './pages/Payroll';
import Attendance from './pages/Attendance';
import Advances from './pages/Advances';
import Leaves from './pages/Leaves';
import Payslips from './pages/Payslips';
import Expenses from './pages/Expenses';
import Finance from './pages/Finance';

const Page = ({ children, perm }) => (
  <ProtectedRoute perm={perm}><Layout>{children}</Layout></ProtectedRoute>
);

// Where to send someone who can't see the Dashboard — their first accessible page.
const LANDINGS = [
  ['/billing', 'sales:create'], ['/sales', 'sales:read'], ['/stock-requests', 'transfers:read'],
  ['/procurement', 'procurement:read'], ['/production', 'production:read'], ['/packing', 'packing:read'],
  ['/inventory', 'inventory:read'], ['/products', 'products:read'], ['/reports', 'reports:read'],
];
const Home = () => {
  const { user, loading, can } = useAuth();
  if (loading) return <div className="spinner" />;
  if (!user) return <Navigate to="/login" replace />;
  if (can('dashboard:read')) return <Page perm="dashboard:read"><Dashboard /></Page>;
  const landing = LANDINGS.find(([, p]) => can(p));
  return landing ? <Navigate to={landing[0]} replace /> : <Page perm="dashboard:read"><Dashboard /></Page>;
};

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
      <CategoriesProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ style: { fontSize: '14px' } }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Home />} />
          <Route path="/billing" element={<Page perm="sales:create"><Billing /></Page>} />
          {/* the till used to live at /pos — keep old links working */}
          <Route path="/pos" element={<Navigate to="/billing" replace />} />
          <Route path="/sales" element={<Page perm="sales:read"><Sales /></Page>} />
          <Route path="/day-close" element={<Page perm="sales:read"><DayClose /></Page>} />
          <Route path="/products" element={<Page perm="products:read"><Products /></Page>} />
          <Route path="/products/:id" element={<Page perm="products:read"><ProductDetail /></Page>} />
          <Route path="/raw-materials" element={<Page perm="rawmaterials:read"><RawMaterials /></Page>} />
          <Route path="/raw-materials/:id" element={<Page perm="rawmaterials:read"><RawMaterialDetail /></Page>} />
          <Route path="/recipes" element={<Page perm="recipes:read"><Recipes /></Page>} />
          <Route path="/recipes/:id" element={<Page perm="recipes:read"><RecipeDetail /></Page>} />
          <Route path="/reports" element={<Page perm="reports:read"><Reports /></Page>} />
          <Route path="/production" element={<Page perm="production:read"><Production /></Page>} />
          <Route path="/packing" element={<Page perm="packing:read"><Packing /></Page>} />
          <Route path="/transfers" element={<Page perm="transfers:write"><Transfers /></Page>} />
          <Route path="/stock-requests" element={<Page perm="transfers:read"><StockRequests /></Page>} />
          <Route path="/procurement" element={<Page perm="procurement:read"><Procurement /></Page>} />
          <Route path="/returns" element={<Page perm="transfers:read"><Returns /></Page>} />
          <Route path="/labels" element={<Page perm="packing:read"><Labels /></Page>} />
          <Route path="/suppliers" element={<Page perm="suppliers:read"><Suppliers /></Page>} />
          <Route path="/suppliers/:id" element={<Page perm="suppliers:read"><SupplierDetail /></Page>} />
          {/* the module used to be called Vendors — keep old links working */}
          <Route path="/vendors" element={<Navigate to="/suppliers" replace />} />
          <Route path="/inventory" element={<Page perm="inventory:read"><Inventory /></Page>} />
          <Route path="/locations" element={<Page perm="locations:read"><Locations /></Page>} />
          <Route path="/employees" element={<Page perm="employees:read"><Employees /></Page>} />
          <Route path="/employees/:id" element={<Page perm="employees:read"><EmployeeDetail /></Page>} />
          <Route path="/attendance" element={<Page perm="employees:read"><Attendance /></Page>} />
          <Route path="/advances" element={<Page perm="employees:read"><Advances /></Page>} />
          <Route path="/leaves" element={<Page perm="employees:read"><Leaves /></Page>} />
          <Route path="/payslips" element={<Page perm="payroll:read"><Payslips /></Page>} />
          <Route path="/payroll" element={<Page perm="payroll:read"><Payroll /></Page>} />
          <Route path="/expenses" element={<Page perm="finance:read"><Expenses /></Page>} />
          <Route path="/finance" element={<Page perm="finance:read"><Finance /></Page>} />
          <Route path="/users" element={<Page perm="users:read"><Users /></Page>} />
          <Route path="/settings" element={<Page perm="settings:read"><Settings /></Page>} />
        </Routes>
      </BrowserRouter>
      </CategoriesProvider>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;
